import React, { useState, useEffect } from 'react';
import { Sidebar } from '@/components/Sidebar';
import { SubscriptionCard } from '@/components/SubscriptionCard';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { MoneyInput } from '@/components/ui/money-input';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { axiosInstance } from '../App';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, Filter, HelpCircle, ChevronDown, X, BookOpen } from 'lucide-react';
import FinancialGlossary from '@/components/FinancialGlossary';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '@/components/ui/dropdown-menu';

// Manual de termos financeiros para lançamentos
const MANUAL_TERMOS = {
  receita: {
    titulo: "Receita",
    emoji: "💰",
    cor: "text-green-400",
    corBg: "bg-green-500/10 border-green-500/30",
    descricao: "São as entradas de dinheiro provenientes da atividade principal da empresa.",
    exemplos: [
      "Pagamento de um cliente por uma obra",
      "Venda de um serviço de reforma",
      "Recebimento de um orçamento aprovado",
      "Entrada de sinal de contrato"
    ],
    impacto: "Aumenta o faturamento e o lucro da empresa.",
    perguntaChave: "Entrou dinheiro de cliente?"
  },
  custo: {
    titulo: "Custos",
    emoji: "🔧",
    cor: "text-orange-400",
    corBg: "bg-orange-500/10 border-orange-500/30",
    descricao: "São gastos diretamente ligados à produção do serviço ou produto. Sem eles, você não consegue entregar o trabalho.",
    exemplos: [
      "Materiais de construção (cimento, tijolos, tinta)",
      "Mão de obra dos pedreiros/pintores",
      "Aluguel de equipamentos para a obra",
      "Transporte de materiais"
    ],
    impacto: "Afeta diretamente o Lucro Bruto e o cálculo do Markup.",
    perguntaChave: "Gastei isso PARA fazer a obra/serviço?"
  },
  despesa: {
    titulo: "Despesas",
    emoji: "📋",
    cor: "text-red-400",
    corBg: "bg-red-500/10 border-red-500/30",
    descricao: "São gastos necessários para manter a empresa funcionando, mas que não estão diretamente ligados a uma obra ou serviço específico.",
    exemplos: [
      "Aluguel do escritório",
      "Conta de luz, água, internet",
      "Salário do administrativo",
      "Contador e honorários"
    ],
    impacto: "Afeta o Lucro Líquido (depois de descontar os custos).",
    perguntaChave: "Gastei isso para MANTER a empresa?"
  },
  despesa_administrativa: {
    titulo: "Despesas Administrativas",
    emoji: "🏢",
    cor: "text-purple-400",
    corBg: "bg-purple-500/10 border-purple-500/30",
    descricao: "São gastos relacionados à gestão e administração geral da empresa, necessários para seu funcionamento independente da produção.",
    exemplos: [
      "Salários de funcionários administrativos",
      "Aluguel do escritório",
      "Material de escritório",
      "Contador e assessoria jurídica",
      "Softwares de gestão",
      "Telefone e internet do escritório"
    ],
    impacto: "São despesas fixas que existem independente do volume de vendas. Devem ser controladas para não comprometer a margem de lucro.",
    perguntaChave: "Esse gasto é para administrar a empresa?"
  },
  despesa_comercial: {
    titulo: "Despesas Comerciais",
    emoji: "📢",
    cor: "text-blue-400",
    corBg: "bg-blue-500/10 border-blue-500/30",
    descricao: "São gastos relacionados à venda e promoção dos produtos ou serviços da empresa.",
    exemplos: [
      "Comissão de vendedores",
      "Marketing e publicidade",
      "Brindes para clientes",
      "Combustível para visitas comerciais",
      "Eventos e feiras",
      "Site e redes sociais"
    ],
    impacto: "Investimento para gerar mais vendas. Deve ser proporcional ao faturamento esperado.",
    perguntaChave: "Esse gasto é para vender mais?"
  },
  despesa_financeira: {
    titulo: "Despesas Financeiras",
    emoji: "🏦",
    cor: "text-yellow-400",
    corBg: "bg-yellow-500/10 border-yellow-500/30",
    descricao: "São gastos relacionados a operações financeiras, empréstimos e serviços bancários.",
    exemplos: [
      "Juros de empréstimos",
      "Taxas bancárias",
      "Juros de cheque especial",
      "Taxas de cartão de crédito",
      "IOF e tarifas",
      "Multas por atraso"
    ],
    impacto: "Reduzem diretamente o lucro. Juros altos podem comprometer seriamente a saúde financeira da empresa.",
    perguntaChave: "Esse gasto é juro, taxa ou tarifa bancária?"
  }
};

const Lancamentos = ({ user, onLogout }) => {
  const [transactions, setTransactions] = useState([]);
  const [filteredTransactions, setFilteredTransactions] = useState([]);
  const [categories, setCategories] = useState({ receita: [], custo: [], despesa: [] });
  const [availableCategories, setAvailableCategories] = useState([]);
  const [selectKey, setSelectKey] = useState(0); // Key para forçar re-render do Select
  const [showDialog, setShowDialog] = useState(false);
  const [editingTransaction, setEditingTransaction] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [loading, setLoading] = useState(false);
  const [showGlossary, setShowGlossary] = useState(false);
  const [showTermoModal, setShowTermoModal] = useState(false);
  const [termoSelecionado, setTermoSelecionado] = useState(null);
  const [pendingFiles, setPendingFiles] = useState([]);
  const [existingDocs, setExistingDocs] = useState([]);
  const [viewingImage, setViewingImage] = useState(null);

  // Função para abrir o modal com o termo selecionado
  const abrirExplicacaoTermo = (termoKey) => {
    setTermoSelecionado(MANUAL_TERMOS[termoKey]);
    setShowTermoModal(true);
  };

  // Formas de pagamento disponíveis
  const FORMAS_PAGAMENTO = [
    { value: 'pix', label: 'PIX' },
    { value: 'dinheiro', label: 'Dinheiro' },
    { value: 'cartao_debito', label: 'Cartão de Débito' },
    { value: 'cartao_credito', label: 'Cartão de Crédito' },
    { value: 'boleto', label: 'Boleto' },
    { value: 'transferencia', label: 'Transferência Bancária' },
    { value: 'cheque', label: 'Cheque' },
  ];

  const [formData, setFormData] = useState({
    type: 'receita',
    description: '',
    amount: 0,  // Valor numérico para MoneyInput
    category_id: '',  // Mudado de 'category' para 'category_id'
    competence_month: new Date().toISOString().slice(0, 7),  // Novo campo: YYYY-MM
    date: new Date().toISOString().slice(0, 10),
    status: 'realizado',
    notes: '',
    // Campos de movimentação de caixa (quando status = realizado)
    forma_pagamento: '',
    data_efetiva_pagamento: new Date().toISOString().slice(0, 10),
    codigo_pagamento: '',
  });

  const company = JSON.parse(localStorage.getItem('company') || '{}');

  useEffect(() => {
    fetchCategories();
    fetchTransactions();
  }, [selectedMonth]);

  useEffect(() => {
    applyFilters();
  }, [transactions, filterType, filterStatus]);

  const fetchCategories = async () => {
    try {
      // Buscar categorias do Plano de Contas
      const response = await axiosInstance.get(`/expense-categories/${company.id}?active_only=true`);
      const planoCategorias = response.data.categories || [];
      
      console.log('📊 Total de categorias carregadas:', planoCategorias.length);
      
      // Organizar por tipo (case-insensitive)
      const organized = {
        receita: planoCategorias.filter(c => c.type?.toLowerCase() === 'receita'),
        custo: planoCategorias.filter(c => c.type?.toLowerCase() === 'custo'),
        despesa: planoCategorias.filter(c => c.type?.toLowerCase() === 'despesa'),
      };
      
      console.log('📋 Categorias organizadas:', {
        receita: organized.receita.length,
        custo: organized.custo.length,
        despesa: organized.despesa.length
      });
      
      setCategories(organized);
      // Inicializar categorias disponíveis baseado no tipo inicial
      updateAvailableCategories('receita', organized);
    } catch (error) {
      console.error('Erro ao carregar categorias:', error);
      toast.error('Erro ao carregar categorias do Plano de Contas');
    }
  };

  // Atualizar categorias disponíveis quando o tipo mudar
  const updateAvailableCategories = (type, categoriesData = categories) => {
    console.log('🔄 updateAvailableCategories chamado:', { 
      type, 
      hasCategoriesData: !!categoriesData,
      keys: categoriesData ? Object.keys(categoriesData) : []
    });
    
    if (!categoriesData) {
      console.log('❌ categoriesData está vazio!');
      setAvailableCategories([]);
      setSelectKey(prev => prev + 1); // Forçar re-render
      return;
    }
    
    if (type === 'receita') {
      const receitaCats = categoriesData.receita || [];
      console.log('✅ Definindo categorias de RECEITA:', receitaCats.length, receitaCats);
      setAvailableCategories(receitaCats);
      setSelectKey(prev => prev + 1); // Forçar re-render
    } else if (type === 'custo') {
      const custoCats = categoriesData.custo || [];
      console.log('✅ Definindo categorias de CUSTO:', custoCats.length, custoCats);
      setAvailableCategories(custoCats);
      setSelectKey(prev => prev + 1); // Forçar re-render
    } else if (type === 'despesa') {
      const despesaCats = categoriesData.despesa || [];
      console.log('✅ Definindo categorias de DESPESA:', despesaCats.length, despesaCats);
      setAvailableCategories(despesaCats);
      setSelectKey(prev => prev + 1); // Forçar re-render
    } else {
      console.log('⚠️ Tipo desconhecido, limpando categorias');
      setAvailableCategories([]);
      setSelectKey(prev => prev + 1); // Forçar re-render
    }
  };

  // Handler para mudança de tipo - limpa categoria e atualiza opções
  const handleTypeChange = (newType) => {
    console.log('🎯 handleTypeChange:', newType);
    setFormData({
      ...formData,
      type: newType,
      category_id: '' // Limpar categoria ao mudar tipo
    });
    updateAvailableCategories(newType, categories);
  };

  // Atualizar categorias disponíveis quando o modal abrir
  useEffect(() => {
    if (showDialog && Object.keys(categories).length > 0) {
      console.log('🚀 Modal aberto, atualizando categorias para tipo:', formData.type);
      updateAvailableCategories(formData.type, categories);
    }
  }, [showDialog]);

  const fetchTransactions = async () => {
    if (!company.id) return;

    try {
      const monthParam = selectedMonth || new Date().getFullYear().toString();
      const response = await axiosInstance.get(`/transactions/${company.id}?month=${monthParam}`);
      setTransactions(response.data);
    } catch (error) {
      toast.error('Erro ao carregar lançamentos');
    }
  };

  const fetchExistingDocs = async (transactionId) => {
    try {
      const res = await axiosInstance.get(`/transactions/${transactionId}/documentos`);
      setExistingDocs(res.data);
    } catch {
      setExistingDocs([]);
    }
  };

  const handleFileAdd = (e) => {
    const files = Array.from(e.target.files);
    const newEntries = files.map((file) => ({ file, previewUrl: URL.createObjectURL(file) }));
    setPendingFiles((prev) => [...prev, ...newEntries]);
    e.target.value = '';
  };

  const handleRemovePending = (index) => {
    setPendingFiles((prev) => {
      URL.revokeObjectURL(prev[index].previewUrl);
      return prev.filter((_, i) => i !== index);
    });
  };

  const handleDeleteExistingDoc = async (transactionId, filename) => {
    try {
      await axiosInstance.delete(`/transactions/${transactionId}/documentos/${filename}`);
      setExistingDocs((prev) => prev.filter((d) => d.filename !== filename));
    } catch {
      toast.error('Erro ao excluir documento');
    }
  };

  const handleViewDoc = async (transactionId, filename) => {
    try {
      const res = await axiosInstance.get(`/transactions/${transactionId}/documentos/${filename}`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const isPdf = filename.toLowerCase().endsWith('.pdf');
      if (isPdf) {
        window.open(url, '_blank');
      } else {
        setViewingImage(url);
      }
    } catch {
      toast.error('Erro ao abrir documento');
    }
  };

  const uploadPendingFiles = async (transactionId) => {
    for (const entry of pendingFiles) {
      const fd = new FormData();
      fd.append('file', entry.file);
      await axiosInstance.post(`/transactions/${transactionId}/documentos`, fd);
    }
  };

  const applyFilters = () => {
    let filtered = [...transactions];

    if (filterType !== 'all') {
      filtered = filtered.filter((t) => t.type === filterType);
    }

    if (filterStatus !== 'all') {
      filtered = filtered.filter((t) => t.status === filterStatus);
    }

    filtered.sort((a, b) => new Date(b.date) - new Date(a.date));

    setFilteredTransactions(filtered);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.category_id) {
      toast.error('Categoria é obrigatória.');
      return;
    }

    // Validação: Se status é realizado, forma de pagamento é obrigatória
    if (formData.status === 'realizado' && !formData.forma_pagamento) {
      toast.error('Para lançamentos realizados, a forma de pagamento é obrigatória.');
      return;
    }

    setLoading(true);

    try {
      const data = {
        company_id: company.id,
        user_id: (user?.id || user?.user_id),
        ...formData,
        amount: parseFloat(formData.amount),
        // Se não for realizado, limpar campos de pagamento
        forma_pagamento: formData.status === 'realizado' ? formData.forma_pagamento : null,
        data_efetiva_pagamento: formData.status === 'realizado' ? formData.data_efetiva_pagamento : null,
      };

      let transactionId;
      if (editingTransaction) {
        await axiosInstance.put(`/transactions/${editingTransaction.id}`, data);
        transactionId = editingTransaction.id;
        toast.success('Lançamento atualizado!');
      } else {
        const res = await axiosInstance.post('/transactions', data);
        transactionId = res.data.transaction_id;
        toast.success('Lançamento criado!');
      }

      if (pendingFiles.length > 0) {
        await uploadPendingFiles(transactionId);
      }

      resetForm();
      fetchTransactions();
    } catch (error) {
      const detail = error?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Erro ao salvar lançamento');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Deseja realmente excluir este lançamento?')) return;

    try {
      await axiosInstance.delete(`/transactions/${id}`);
      toast.success('Lançamento excluído!');
      fetchTransactions();
    } catch (error) {
      toast.error('Erro ao excluir lançamento');
    }
  };

  const handleEdit = (transaction) => {
    setEditingTransaction(transaction);
    // Atualizar categorias disponíveis baseado no tipo da transação
    updateAvailableCategories(transaction.type, categories);
    setFormData({
      type: transaction.type,
      description: transaction.description,
      amount: parseFloat(transaction.amount) || 0,  // Valor numérico
      category_id: transaction.category_id || '',
      competence_month: transaction.competence_month || new Date().toISOString().slice(0, 7),
      date: transaction.date,
      status: transaction.status,
      notes: transaction.notes || '',
      // Campos de movimentação de caixa
      forma_pagamento: transaction.forma_pagamento || '',
      data_efetiva_pagamento: transaction.data_efetiva_pagamento || transaction.date || new Date().toISOString().slice(0, 10),
      codigo_pagamento: transaction.codigo_pagamento || '',
    });
    setPendingFiles([]);
    fetchExistingDocs(transaction.id);
    setShowDialog(true);
  };

  const resetForm = () => {
    setFormData({
      type: 'receita',
      description: '',
      amount: 0,  // Valor numérico
      category_id: '',
      competence_month: new Date().toISOString().slice(0, 7),
      date: new Date().toISOString().slice(0, 10),
      status: 'realizado',
      notes: '',
      // Campos de movimentação de caixa
      forma_pagamento: '',
      data_efetiva_pagamento: new Date().toISOString().slice(0, 10),
      codigo_pagamento: '',
    });
    // Resetar categorias para receita (tipo padrão)
    updateAvailableCategories('receita', categories);
    setEditingTransaction(null);
    setPendingFiles([]);
    setExistingDocs([]);
    setShowDialog(false);
  };

  const openNewTransactionModal = async () => {
    setEditingTransaction(null);
    
    // Garantir que as categorias estejam carregadas
    if (!categories || Object.keys(categories).length === 0 || !categories.receita) {
      console.log('⚠️ Categorias não carregadas, buscando...');
      await fetchCategories();
      // Aguardar um pouco para o estado atualizar
      await new Promise(resolve => setTimeout(resolve, 500));
    }
    
    setFormData({
      type: 'receita',
      description: '',
      amount: 0,  // Valor numérico
      category_id: '',
      competence_month: new Date().toISOString().slice(0, 7),
      date: new Date().toISOString().slice(0, 10),
      status: 'realizado',
      notes: '',
    });
    
    // Atualizar categorias disponíveis para o tipo padrão (receita)
    console.log('🆕 Abrindo modal de novo lançamento');
    console.log('📊 Categorias disponíveis:', categories);
    
    // Usar setTimeout para garantir que o estado foi atualizado
    setTimeout(() => {
      updateAvailableCategories('receita', categories);
    }, 100);
    
    setShowDialog(true);
  };

  const getCategoryOptions = () => {
    // Retorna categorias baseado no tipo selecionado
    return availableCategories;
  };

  const getTypeLabel = (type) => {
    const labels = { receita: 'Receita', custo: 'Custo', despesa: 'Despesa' };
    return labels[type] || type;
  };

  const getTypeBadgeColor = (type) => {
    const colors = {
      receita: 'bg-green-500/20 text-green-300 border-green-500/50',
      custo: 'bg-orange-500/20 text-orange-300 border-orange-500/50',
      despesa: 'bg-red-500/20 text-red-300 border-red-500/50',
    };
    return colors[type] || 'bg-gray-500/20 text-gray-300 border-gray-500/50';
  };

  const getStatusBadgeColor = (status) => {
    return status === 'realizado'
      ? 'bg-blue-500/20 text-blue-300 border-blue-500/50'
      : 'bg-yellow-500/20 text-yellow-300 border-yellow-500/50';
  };

  return (
    <div className="flex min-h-screen" data-testid="lancamentos-page">
      <Sidebar user={user} onLogout={onLogout} onOpenGlossary={() => setShowGlossary(true)} />

      <div className="flex-1 p-8">
        <SubscriptionCard user={user} />

        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-4xl font-bold text-white mb-2" data-testid="lancamentos-title">Lançamentos</h1>
            <p className="text-gray-400">Gerencie receitas, custos e despesas</p>
          </div>
          <Button
            onClick={openNewTransactionModal}
            data-testid="add-transaction-button"
            className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700"
          >
            <Plus className="mr-2" size={18} />
            Novo Lançamento
          </Button>
        </div>

        {/* Filtros */}
        <Card className="glass border-white/10 mb-6" data-testid="filters-card">
          <CardContent className="p-4">
            <div className="flex gap-4 items-center">
              <Filter size={20} className="text-gray-400" />
              <div className="flex-1 grid grid-cols-3 gap-4">
                <div>
                  <Label className="text-gray-400 text-sm">Mês</Label>
                  <div className="relative">
                    <Input
                      type="month"
                      data-testid="filter-month-input"
                      value={selectedMonth}
                      onChange={(e) => setSelectedMonth(e.target.value)}
                      className="bg-white/5 border-white/10 text-white pr-8"
                    />
                    {selectedMonth && (
                      <button
                        type="button"
                        onClick={() => setSelectedMonth('')}
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
                      >
                        <X size={14} />
                      </button>
                    )}
                  </div>
                </div>
                <div>
                  <Label className="text-gray-400 text-sm">Tipo</Label>
                  <Select value={filterType} onValueChange={setFilterType}>
                    <SelectTrigger className="bg-white/5 border-white/10 text-white" data-testid="filter-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos</SelectItem>
                      <SelectItem value="receita">Receitas</SelectItem>
                      <SelectItem value="custo">Custos</SelectItem>
                      <SelectItem value="despesa">Despesas</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-gray-400 text-sm">Status</Label>
                  <Select value={filterStatus} onValueChange={setFilterStatus}>
                    <SelectTrigger className="bg-white/5 border-white/10 text-white" data-testid="filter-status-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos</SelectItem>
                      <SelectItem value="previsto">Previsto</SelectItem>
                      <SelectItem value="realizado">Realizado</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Tabela */}
        <Card className="glass border-white/10" data-testid="transactions-table-card">
          <CardHeader>
            <CardTitle className="text-white">Lançamentos ({filteredTransactions.length})</CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow className="border-white/10 hover:bg-white/5">
                  <TableHead className="text-gray-300">Data</TableHead>
                  <TableHead className="text-gray-300">Tipo</TableHead>
                  <TableHead className="text-gray-300">Fornecedor</TableHead>
                  <TableHead className="text-gray-300">Descrição</TableHead>
                  <TableHead className="text-gray-300">Categoria</TableHead>
                  <TableHead className="text-gray-300 text-right">Valor</TableHead>
                  <TableHead className="text-gray-300">Status</TableHead>
                  <TableHead className="text-gray-300">Forma Pgto.</TableHead>
                  <TableHead className="text-gray-300">Vencimento</TableHead>
                  <TableHead className="text-gray-300 text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredTransactions.map((transaction) => (
                  <TableRow key={transaction.id} className="border-white/10 hover:bg-white/5" data-testid={`transaction-row-${transaction.id}`}>
                    <TableCell className="text-gray-300">
                      {transaction.date ? transaction.date.split('-').reverse().join('/') : '-'}
                    </TableCell>
                    <TableCell>
                      <Badge className={getTypeBadgeColor(transaction.type)}>
                        {getTypeLabel(transaction.type)}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-gray-300 text-sm">{transaction.fornecedor_nome || '—'}</TableCell>
                    <TableCell className="text-white font-medium">{transaction.description}</TableCell>
                    <TableCell className="text-gray-400 text-sm">{transaction.category}</TableCell>
                    <TableCell className="text-white text-right font-semibold">
                      R$ {transaction.amount.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                    </TableCell>
                    <TableCell>
                      <Badge className={getStatusBadgeColor(transaction.status)}>
                        {transaction.status === 'realizado' ? 'Realizado' : 'Previsto'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-gray-400 text-sm">
                      {transaction.status === 'realizado' && transaction.forma_pagamento ? (
                        <span className="text-green-400">
                          {FORMAS_PAGAMENTO.find(fp => fp.value === transaction.forma_pagamento)?.label || transaction.forma_pagamento}
                        </span>
                      ) : (
                        <span className="text-gray-500">-</span>
                      )}
                    </TableCell>
                    <TableCell className="text-gray-400 text-sm">
                      {transaction.data_vencimento ? (
                        transaction.data_vencimento.split('-').reverse().join('/')
                      ) : (
                        <span className="text-gray-500">-</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleEdit(transaction)}
                          data-testid={`edit-transaction-${transaction.id}`}
                          className="border-blue-500/50 text-blue-400 hover:bg-blue-500/10"
                        >
                          <Edit size={14} />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleDelete(transaction.id)}
                          data-testid={`delete-transaction-${transaction.id}`}
                          className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                        >
                          <Trash2 size={14} />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
                {filteredTransactions.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={10} className="text-center text-gray-400 py-8" data-testid="no-transactions-message">
                      Nenhum lançamento encontrado
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* Dialog */}
        <Dialog open={showDialog} onOpenChange={(open) => !open && resetForm()}>
          <DialogContent className="glass border-white/10 max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="transaction-dialog">
            <DialogHeader>
              <DialogTitle className="text-white flex items-center gap-3">
                {editingTransaction ? 'Editar Lançamento' : 'Novo Lançamento'}
                
                {/* Botão de Ajuda com Dropdown */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button 
                      type="button"
                      className="flex items-center gap-1 px-2 py-1 rounded-lg bg-blue-500/20 hover:bg-blue-500/30 border border-blue-500/30 text-blue-400 text-sm transition-colors"
                      data-testid="help-dropdown-trigger"
                    >
                      <HelpCircle className="w-4 h-4" />
                      <ChevronDown className="w-3 h-3" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent className="bg-zinc-900 border-zinc-700 min-w-[220px]">
                    <div className="px-2 py-1.5 text-xs text-gray-400 font-medium">
                      <BookOpen className="w-3 h-3 inline mr-1" />
                      Manual de Termos
                    </div>
                    <DropdownMenuSeparator className="bg-zinc-700" />
                    <DropdownMenuItem 
                      onClick={() => abrirExplicacaoTermo('receita')}
                      className="text-green-400 hover:bg-green-500/10 cursor-pointer"
                    >
                      💰 Receita
                    </DropdownMenuItem>
                    <DropdownMenuItem 
                      onClick={() => abrirExplicacaoTermo('custo')}
                      className="text-orange-400 hover:bg-orange-500/10 cursor-pointer"
                    >
                      🔧 Custos
                    </DropdownMenuItem>
                    <DropdownMenuItem 
                      onClick={() => abrirExplicacaoTermo('despesa')}
                      className="text-red-400 hover:bg-red-500/10 cursor-pointer"
                    >
                      📋 Despesas
                    </DropdownMenuItem>
                    <DropdownMenuSeparator className="bg-zinc-700" />
                    <div className="px-2 py-1.5 text-xs text-gray-500">
                      Tipos de Despesas
                    </div>
                    <DropdownMenuItem 
                      onClick={() => abrirExplicacaoTermo('despesa_administrativa')}
                      className="text-purple-400 hover:bg-purple-500/10 cursor-pointer"
                    >
                      🏢 Despesas Administrativas
                    </DropdownMenuItem>
                    <DropdownMenuItem 
                      onClick={() => abrirExplicacaoTermo('despesa_comercial')}
                      className="text-blue-400 hover:bg-blue-500/10 cursor-pointer"
                    >
                      📢 Despesas Comerciais
                    </DropdownMenuItem>
                    <DropdownMenuItem 
                      onClick={() => abrirExplicacaoTermo('despesa_financeira')}
                      className="text-yellow-400 hover:bg-yellow-500/10 cursor-pointer"
                    >
                      🏦 Despesas Financeiras
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="transaction-form">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-300">Tipo *</Label>
                  <Select
                    value={formData.type}
                    onValueChange={handleTypeChange}
                  >
                    <SelectTrigger className="bg-white/5 border-white/10 text-white" data-testid="transaction-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="receita">Receita</SelectItem>
                      <SelectItem value="custo">Custo</SelectItem>
                      <SelectItem value="despesa">Despesa</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-gray-300">
                    Categoria * 
                    {availableCategories.length === 0 && <span className="text-xs text-gray-500 ml-2">(selecione o tipo primeiro)</span>}
                  </Label>
                  <Select 
                    key={`category-select-${selectKey}`}
                    value={formData.category_id} 
                    onValueChange={(value) => setFormData({ ...formData, category_id: value })}
                    disabled={availableCategories.length === 0}
                  >
                    <SelectTrigger className="bg-white/5 border-white/10 text-white" data-testid="transaction-category-select">
                      <SelectValue placeholder={availableCategories.length === 0 ? "Selecione o tipo primeiro" : "Selecione a categoria"} />
                    </SelectTrigger>
                    <SelectContent>
                      {availableCategories.map((cat) => (
                        <SelectItem key={cat.id} value={cat.id}>
                          {cat.name}
                          {cat.is_indirect_for_markup && <span className="ml-2 text-xs text-blue-400">(Markup)</span>}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label className="text-gray-300">Descrição</Label>
                <Input
                  data-testid="transaction-description-input"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  required
                  className="bg-white/5 border-white/10 text-white"
                />
              </div>

              <div className="grid grid-cols-3 gap-4 items-end">
                <div>
                  <Label className="text-gray-300">Valor *</Label>
                  <MoneyInput
                    data-testid="transaction-amount-input"
                    value={formData.amount}
                    onChange={(value) => setFormData({ ...formData, amount: value })}
                    className="bg-white/5 border-white/10 text-white h-10"
                  />
                </div>
                <div>
                  <Label className="text-gray-300 flex items-center gap-1">
                    Competência *
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <HelpCircle className="w-3.5 h-3.5 text-blue-400 cursor-help" />
                        </TooltipTrigger>
                        <TooltipContent side="top" className="max-w-[250px] bg-zinc-800 border-zinc-700">
                          <p className="text-sm">
                            <strong>Mês de Apuração:</strong> É o mês em que esse lançamento será considerado nos relatórios financeiros, 
                            independente da data de pagamento/recebimento.
                          </p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </Label>
                  <Input
                    type="month"
                    data-testid="transaction-competence-input"
                    value={formData.competence_month}
                    onChange={(e) => setFormData({ ...formData, competence_month: e.target.value })}
                    required
                    className="bg-white/5 border-white/10 text-white h-10"
                  />
                </div>
                <div>
                  <Label className="text-gray-300">Data</Label>
                  <Input
                    type="date"
                    data-testid="transaction-date-input"
                    value={formData.date}
                    onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                    required
                    className="bg-white/5 border-white/10 text-white h-10"
                  />
                </div>
              </div>

              <div>
                <Label className="text-gray-300">Status</Label>
                <Select value={formData.status} onValueChange={(value) => setFormData({ ...formData, status: value })}>
                  <SelectTrigger className="bg-white/5 border-white/10 text-white" data-testid="transaction-status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="realizado">Realizado</SelectItem>
                    <SelectItem value="previsto">Previsto</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Campos condicionais para quando status = "realizado" */}
              {formData.status === 'realizado' && (
                <div className="p-4 rounded-lg bg-green-500/10 border border-green-500/30 space-y-4">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse"></div>
                    <span className="text-green-400 font-medium text-sm">
                      {formData.type === 'receita' ? '💰 Recebimento Efetivado' : '💸 Pagamento Efetivado'}
                    </span>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-gray-300">
                        Forma de {formData.type === 'receita' ? 'Recebimento' : 'Pagamento'} *
                      </Label>
                      <Select 
                        value={formData.forma_pagamento} 
                        onValueChange={(value) => setFormData({ ...formData, forma_pagamento: value })}
                      >
                        <SelectTrigger className="bg-white/5 border-white/10 text-white" data-testid="transaction-forma-pagamento-select">
                          <SelectValue placeholder="Selecione..." />
                        </SelectTrigger>
                        <SelectContent>
                          {FORMAS_PAGAMENTO.map((fp) => (
                            <SelectItem key={fp.value} value={fp.value}>
                              {fp.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div>
                      <Label className="text-gray-300">
                        Data do {formData.type === 'receita' ? 'Recebimento' : 'Pagamento'}
                      </Label>
                      <Input
                        type="date"
                        data-testid="transaction-data-efetiva-input"
                        value={formData.data_efetiva_pagamento}
                        onChange={(e) => setFormData({ ...formData, data_efetiva_pagamento: e.target.value })}
                        className="bg-white/5 border-white/10 text-white"
                      />
                    </div>
                  </div>
                  
                  <p className="text-xs text-gray-400 mt-2">
                    ⚠️ Lançamentos realizados impactam diretamente o fluxo de caixa e relatórios financeiros da empresa.
                  </p>
                </div>
              )}

              {/* Aviso para status previsto */}
              {formData.status === 'previsto' && (
                <div className="p-3 rounded-lg bg-yellow-500/10 border border-yellow-500/30">
                  <p className="text-xs text-yellow-400">
                    📅 Lançamentos previstos não afetam o caixa atual. Use para planejar receitas e despesas futuras.
                  </p>
                </div>
              )}

              <div>
                <Label className="text-gray-300">Observações (Opcional)</Label>
                <Textarea
                  data-testid="transaction-notes-input"
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  className="bg-white/5 border-white/10 text-white"
                  rows={2}
                />
              </div>

              {/* Código de boleto / PIX */}
              <div>
                <Label className="text-gray-300">Código de Boleto / PIX (Opcional)</Label>
                <Input
                  value={formData.codigo_pagamento}
                  onChange={(e) => setFormData({ ...formData, codigo_pagamento: e.target.value })}
                  placeholder="Cole aqui o código de barras ou chave PIX"
                  className="bg-white/5 border-white/10 text-white font-mono text-sm"
                />
              </div>

              {/* Documentos */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="text-gray-300">Documentos (PDF, PNG, JPG)</Label>
                  <label className="cursor-pointer flex items-center gap-1 px-3 py-1 rounded-lg bg-white/10 hover:bg-white/20 text-white text-sm transition-colors">
                    <Plus size={14} />
                    Adicionar
                    <input
                      type="file"
                      accept=".pdf,.png,.jpg,.jpeg"
                      multiple
                      className="hidden"
                      onChange={handleFileAdd}
                    />
                  </label>
                </div>

                {/* Docs já salvos (edição) */}
                {existingDocs.length > 0 && (
                  <div className="space-y-1">
                    {existingDocs.map((doc) => (
                      <div key={doc.filename} className="flex items-center justify-between px-3 py-2 rounded-lg bg-white/5 border border-white/10">
                        <span className="text-gray-300 text-sm truncate max-w-[260px]">{doc.filename}</span>
                        <div className="flex gap-2">
                          <button
                            type="button"
                            onClick={() => handleViewDoc(editingTransaction.id, doc.filename)}
                            className="text-blue-400 hover:text-blue-300 text-xs"
                          >
                            Ver
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDeleteExistingDoc(editingTransaction.id, doc.filename)}
                            className="text-red-400 hover:text-red-300"
                          >
                            <Trash2 size={13} />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Arquivos pendentes (ainda não enviados) */}
                {pendingFiles.length > 0 && (
                  <div className="space-y-1">
                    {pendingFiles.map((entry, index) => (
                      <div key={index} className="flex items-center justify-between px-3 py-2 rounded-lg bg-blue-500/10 border border-blue-500/20">
                        <span className="text-blue-300 text-sm truncate max-w-[260px]">{entry.file.name}</span>
                        <button
                          type="button"
                          onClick={() => handleRemovePending(index)}
                          className="text-red-400 hover:text-red-300"
                        >
                          <X size={13} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="flex gap-3 justify-end">
                <Button type="button" variant="outline" onClick={resetForm} data-testid="transaction-cancel-button">
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  data-testid="transaction-submit-button"
                  disabled={loading || (formData.status === 'realizado' && !formData.forma_pagamento)}
                  className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700"
                >
                  {loading ? 'Salvando...' : 'Salvar'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Modal de Explicação do Termo */}
        <Dialog open={showTermoModal} onOpenChange={setShowTermoModal}>
          <DialogContent className="glass border-white/10 max-w-lg">
            {termoSelecionado && (
              <>
                <DialogHeader>
                  <DialogTitle className={`text-xl font-bold flex items-center gap-2 ${termoSelecionado.cor}`}>
                    <span className="text-2xl">{termoSelecionado.emoji}</span>
                    {termoSelecionado.titulo}
                  </DialogTitle>
                </DialogHeader>
                
                <div className="space-y-4">
                  {/* Descrição */}
                  <div className={`p-4 rounded-lg border ${termoSelecionado.corBg}`}>
                    <p className="text-gray-200 text-sm leading-relaxed">
                      {termoSelecionado.descricao}
                    </p>
                  </div>

                  {/* Pergunta-chave */}
                  <div className="p-3 rounded-lg bg-white/5 border border-white/10">
                    <p className="text-xs text-gray-400 mb-1">🎯 Pergunta-chave:</p>
                    <p className={`font-medium ${termoSelecionado.cor}`}>
                      "{termoSelecionado.perguntaChave}"
                    </p>
                  </div>

                  {/* Exemplos */}
                  <div>
                    <p className="text-sm text-gray-400 mb-2 font-medium">📝 Exemplos:</p>
                    <ul className="space-y-1.5">
                      {termoSelecionado.exemplos.map((exemplo, index) => (
                        <li key={index} className="flex items-start gap-2 text-sm text-gray-300">
                          <span className={`mt-1.5 w-1.5 h-1.5 rounded-full flex-shrink-0 ${termoSelecionado.cor.replace('text-', 'bg-')}`}></span>
                          {exemplo}
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Impacto */}
                  <div className="p-3 rounded-lg bg-zinc-800/50 border border-zinc-700">
                    <p className="text-xs text-gray-400 mb-1">📊 Impacto no negócio:</p>
                    <p className="text-sm text-gray-200">
                      {termoSelecionado.impacto}
                    </p>
                  </div>

                  {/* Botão Fechar */}
                  <Button 
                    onClick={() => setShowTermoModal(false)}
                    className="w-full bg-white/10 hover:bg-white/20 text-white"
                  >
                    Entendi
                  </Button>
                </div>
              </>
            )}
          </DialogContent>
        </Dialog>
      </div>
      
      {/* Glossário Financeiro */}
      <FinancialGlossary isOpen={showGlossary} onClose={() => setShowGlossary(false)} />

      {/* Modal de visualização de imagem */}
      {viewingImage && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80"
          onClick={() => { URL.revokeObjectURL(viewingImage); setViewingImage(null); }}
        >
          <img
            src={viewingImage}
            alt="Documento"
            className="max-w-[90vw] max-h-[90vh] rounded-lg shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />
          <button
            className="absolute top-4 right-4 text-white bg-black/50 rounded-full p-1 hover:bg-black/80"
            onClick={() => { URL.revokeObjectURL(viewingImage); setViewingImage(null); }}
          >
            <X size={20} />
          </button>
        </div>
      )}
    </div>
  );
};

export default Lancamentos;
